import { useState, useCallback, useMemo } from "react";
import type { SortDescriptor } from "@heroui/react";
import type { Order, OrderGroup } from "@/components/orderList/types";

type SortDirection = "ascending" | "descending";

function compareNaNValues(
  first: number | string,
  second: number | string,
  direction: SortDirection
): number | null {
  const isFirstNaN = Number.isNaN(first as number);
  const isSecondNaN = Number.isNaN(second as number);

  if (isFirstNaN && isSecondNaN) return 0;
  if (isFirstNaN) return direction === "ascending" ? -1 : 1;
  if (isSecondNaN) return direction === "ascending" ? 1 : -1;
  return null;
}

function compareValues(
  first: number | string,
  second: number | string,
  direction: SortDirection,
  isDeadlineColumn: boolean
): number {
  if (isDeadlineColumn) {
    const nanComparison = compareNaNValues(first, second, direction);
    if (nanComparison !== null) return nanComparison;

    first = Number.isNaN(first as number) ? Number.MAX_SAFE_INTEGER : first;
    second = Number.isNaN(second as number) ? Number.MAX_SAFE_INTEGER : second;
  }

  let cmp = 0;
  if (first < second) {
    cmp = -1;
  } else if (first > second) {
    cmp = 1;
  }
  return direction === "descending" ? -cmp : cmp;
}

interface UseOrdersGroupingProps {
  filteredItems: Order[];
  sortDescriptor: SortDescriptor;
  rowsPerPage: number;
  page: number;
}

interface UseOrdersGroupingResult {
  paginatedGroups: OrderGroup[];
  pages: number;
  expandedGroups: Set<string>;
  toggleGroup: (jobID_parent: string) => void;
}

/**
 * Hook to manage order grouping, sorting, and pagination
 */
export const useOrdersGrouping = ({
  filteredItems,
  sortDescriptor,
  rowsPerPage,
  page,
}: UseOrdersGroupingProps): UseOrdersGroupingResult => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Toggle accordion expansion
  const toggleGroup = useCallback((jobID_parent: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobID_parent)) {
        newSet.delete(jobID_parent);
      } else {
        newSet.add(jobID_parent);
      }
      return newSet;
    });
  }, []);

  // Group orders by jobID_parent
  const groupOrders = useCallback((ordersList: Order[]): OrderGroup[] => {
    const groups = new Map<string, OrderGroup>();

    ordersList.forEach((order) => {
      const parentId = order.jobID_parent;
      if (!groups.has(parentId)) {
        groups.set(parentId, {
          jobID_parent: parentId,
          orders: [],
        });
      }
      groups.get(parentId)!.orders.push(order);
    });

    // Sort orders within each group by start_datetime
    groups.forEach((group) => {
      group.orders.sort(
        (a, b) => new Date(a.start_datetime).getTime() - new Date(b.start_datetime).getTime()
      );
      // Add index information to each order
      group.orders = group.orders.map((order, index) => ({
        ...order,
        groupIndex: index + 1,
        groupTotal: group.orders.length,
      }));
    });

    return Array.from(groups.values());
  }, []);

  // Sort items
  const sortedItems = useMemo(() => {
    const isDeadlineColumn = sortDescriptor.column === "deadline";
    const direction = sortDescriptor.direction as SortDirection;

    const getComparableValue = (item: Order): number | string => {
      if (isDeadlineColumn) {
        return item.status === "pending_review" ? item.deadline : Number.MAX_SAFE_INTEGER;
      }
      return item[sortDescriptor.column as keyof Order] as number | string;
    };

    return [...filteredItems].sort((a, b) => {
      const first = getComparableValue(a);
      const second = getComparableValue(b);
      return compareValues(first, second, direction, isDeadlineColumn);
    });
  }, [sortDescriptor, filteredItems]);

  // Group sorted items
  const groupedItems = useMemo(() => {
    return groupOrders(sortedItems);
  }, [sortedItems, groupOrders]);

  // Sort groups by the first order's sort value
  const sortedGroups = useMemo(() => {
    const isDeadlineColumn = sortDescriptor.column === "deadline";
    const direction = sortDescriptor.direction as SortDirection;

    const getComparableValue = (group: OrderGroup): number | string => {
      const firstOrder = group.orders[0];
      if (isDeadlineColumn) {
        const pendingDeadlines = group.orders
          .filter((o) => o.status === "pending_review")
          .map((o) => o.deadline);
        return pendingDeadlines.length > 0 ? Math.min(...pendingDeadlines) : Number.MAX_SAFE_INTEGER;
      }
      return firstOrder[sortDescriptor.column as keyof Order] as number | string;
    };

    return [...groupedItems].sort((a, b) => {
      const first = getComparableValue(a);
      const second = getComparableValue(b);
      return compareValues(first, second, direction, isDeadlineColumn);
    });
  }, [groupedItems, sortDescriptor]);

  // Calculate pages based on grouped items
  const pages = Math.ceil(sortedGroups.length / rowsPerPage);

  // Paginate groups
  const paginatedGroups = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedGroups.slice(start, end);
  }, [page, sortedGroups, rowsPerPage]);

  return {
    paginatedGroups,
    pages,
    expandedGroups,
    toggleGroup,
  };
};

export default useOrdersGrouping;
